import { useState } from 'react';
import { apiChangeStock, type Variant } from '~/lib/api/shop';
import { BTN_GHOST, BTN_SECONDARY, ErrorBanner, Segmented, Spinner } from '~/components/workbench/tools/payments/ui';
import { apiErrorMessage, parseCount, parseDelta } from './shop-model';

type Mode = 'set' | 'delta';

/**
 * Stock editor: "Count" sets on-hand stock, "+/−" adjusts it. Every change is
 * a ledger row on the server, with an optional note. Either starts tracking an
 * untracked variant.
 */
export function StockControl({
  appId,
  variant,
  onChanged,
  defaultMode = 'set',
}: {
  appId: string;
  variant: Variant;
  onChanged: (next: Variant) => void;
  defaultMode?: Mode;
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>(defaultMode);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    const nextMode = variant.stock === null ? 'set' : defaultMode;
    setMode(nextMode);
    setAmount(nextMode === 'set' && variant.stock !== null ? String(variant.stock) : '');
    setNote('');
    setError(null);
    setOpen(true);
  };

  const apply = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const value = mode === 'set' ? parseCount(amount) : parseDelta(amount);

    if (value === null) {
      setError(mode === 'set' ? 'Enter the count on hand, e.g. 12.' : 'Enter a change like +5 or -2.');
      return;
    }

    const current = variant.stock ?? 0;

    if (mode === 'delta' && variant.stock !== null && current + value < 0) {
      setError(`Stock can't go below zero (on hand ${current}).`);
      return;
    }

    setBusy(true);

    try {
      const trimmed = note.trim() || undefined;
      const next = await apiChangeStock(
        appId,
        variant.id,
        mode === 'set' ? { set: value, note: trimmed } : { delta: value, note: trimmed },
      );
      onChanged(next);
      setOpen(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not change stock.'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={start} className={BTN_GHOST}>
        <div className="i-ph:stack text-[13px]" />
        {variant.stock === null ? 'Track stock' : 'Stock'}
      </button>
    );
  }

  return (
    <form onSubmit={apply} className="mt-2 w-full space-y-2 rounded-lg border border-hairline bg-surface p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          label="Stock change"
          value={mode}
          options={[
            ['set', 'Count'],
            ['delta', '+/−'],
          ]}
          onChange={(m) => {
            setMode(m);
            setAmount(m === 'set' && variant.stock !== null ? String(variant.stock) : '');
          }}
        />
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="numeric"
          autoFocus
          placeholder={mode === 'set' ? 'On hand' : '+5 or -2'}
          aria-label={mode === 'set' ? 'Count on hand' : 'Change in stock'}
          className="ui-input-mono !w-24 !py-1.5"
        />
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional), e.g. restock from supplier"
        aria-label="Note"
        className="ui-input !py-1.5 !text-[12px]"
      />
      {error && <ErrorBanner message={error} />}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy} className={BTN_SECONDARY}>
          {busy && <Spinner />}
          {mode === 'set' ? 'Save count' : 'Apply change'}
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy} className={BTN_GHOST}>
          Cancel
        </button>
      </div>
    </form>
  );
}
